'use client';

import { Check } from 'lucide-react';
import { PHASES } from '@/lib/interview/topic-definitions';
import type { PhaseId } from '@/lib/interview/types';

export const PHASE_STEPS: { id: PhaseId; label: string; hint: string }[] = [
  { id: 'discovery', label: 'Kennismaking', hint: 'Wie je bent en wat je zoekt' },
  { id: 'deep_dive', label: 'Verdieping', hint: 'Jullie werk, knelpunten en plannen' },
  { id: 'summary', label: 'Afsluiting', hint: 'Samenvatten en je rapport' },
];

function areasOf(phase: PhaseId) {
  return PHASES.find((p) => p.id === phase)?.guidanceAreas ?? [];
}

interface PhaseStepperProps {
  currentPhase: PhaseId;
  /** 0 to 1, how far the current phase is */
  progressInPhase?: number;
  isCompleting?: boolean;
  /** Topic ids per phase that already got a concrete answer */
  coveredAreas?: Partial<Record<PhaseId, string[]>>;
}

/**
 * Three steps of the conversation with the topics inside each, so people see
 * where they are and what is still to come. The number of questions varies;
 * the topics don't.
 */
export function PhaseStepper({
  currentPhase,
  progressInPhase = 0,
  isCompleting = false,
  coveredAreas = {},
}: PhaseStepperProps) {
  const activeIndex = Math.max(0, PHASE_STEPS.findIndex((s) => s.id === currentPhase));

  const isCovered = (phaseIndex: number, areaId: string) =>
    isCompleting ||
    phaseIndex < activeIndex ||
    (coveredAreas[PHASE_STEPS[phaseIndex].id] || []).includes(areaId);

  const activeAreas = areasOf(PHASE_STEPS[activeIndex].id);

  return (
    <div>
      <ol className="grid grid-cols-3 gap-2 sm:gap-3" aria-label="Stappen van het gesprek">
        {PHASE_STEPS.map((step, i) => {
          const done = isCompleting || i < activeIndex;
          const active = !isCompleting && i === activeIndex;
          const fill = done ? 100 : active ? Math.max(8, Math.min(100, Math.round(progressInPhase * 100))) : 0;

          return (
            <li key={step.id} aria-current={active ? 'step' : undefined} className="min-w-0">
              <div className="h-1 rounded-full bg-off-black/10 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-[width] duration-500 ease-out ${
                    done ? 'bg-off-black' : 'bg-orange-cta'
                  }`}
                  style={{ width: `${fill}%` }}
                />
              </div>
              <div className="mt-2 flex items-center gap-1.5 min-w-0">
                <span
                  className={`flex-shrink-0 inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-medium ${
                    done
                      ? 'bg-off-black text-off-white'
                      : active
                        ? 'bg-orange-cta text-white'
                        : 'border border-off-black/20 text-off-black/40'
                  }`}
                  aria-hidden="true"
                >
                  {done ? <Check className="w-2.5 h-2.5" strokeWidth={3} /> : i + 1}
                </span>
                <span
                  className={`truncate text-xs sm:text-sm ${
                    active ? 'text-off-black font-medium' : done ? 'text-off-black/70' : 'text-off-black/40'
                  }`}
                >
                  {step.label}
                </span>
              </div>
              {/* Desktop: the topics of every step */}
              <ul className="hidden sm:flex flex-wrap gap-x-2.5 gap-y-0.5 mt-1 pl-[22px]">
                {areasOf(step.id).map((area) => {
                  const covered = isCovered(i, area.id);
                  return (
                    <li
                      key={area.id}
                      className={`inline-flex items-center gap-1 text-xs ${
                        covered ? 'text-off-black/70' : active ? 'text-off-black/45' : 'text-off-black/30'
                      }`}
                    >
                      {covered && <Check className="w-3 h-3" aria-hidden="true" />}
                      {area.name}
                      <span className="sr-only">{covered ? ' (besproken)' : ' (nog niet besproken)'}</span>
                    </li>
                  );
                })}
              </ul>
            </li>
          );
        })}
      </ol>

      {/* Mobile: only the topics of the current step */}
      {!isCompleting && (
        <ul className="sm:hidden flex flex-wrap gap-1.5 mt-2.5" aria-label="Onderwerpen in deze stap">
          {activeAreas.map((area) => {
            const covered = isCovered(activeIndex, area.id);
            return (
              <li
                key={area.id}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${
                  covered
                    ? 'bg-off-black text-off-white border-off-black'
                    : 'border-off-black/15 text-off-black/55'
                }`}
              >
                {covered && <Check className="w-3 h-3" aria-hidden="true" />}
                {area.name}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
